/**
 * Renders appointment manage overlays: floating details, backdrop dismiss, and manual reschedule.
 */

import React, { useState } from 'react';
import type { Appointment } from '@/api/mcp-appointments';
import { AppointmentDetailsPopup } from './AppointmentDetailsPopup';
import { AppointmentNoteEditModal } from './AppointmentNoteEditModal';
import { useAppointmentClientContact } from './useAppointmentClientContact';
import ManualRescheduleDialog from './ManualRescheduleDialog';
import {
  AppointmentChangeRequestReviewModal,
  isRescheduleChangeRequest
} from './AppointmentChangeRequestReviewModal';
import type { AppointmentManagePhase } from './useAppointmentManageFlow';

/** Inputs wired from `useAppointmentManageFlow` into the manage overlay tree. */
interface AppointmentManageOverlaysProps {
  /** Appointment currently shown in details or reschedule UI. */
  activeAppointment: Appointment | null;
  /** Whether the floating details popup should render. */
  detailsOpen: boolean;
  /** Whether Floating UI has finished positioning the details popup. */
  detailsPositioned?: boolean;
  /** Active manage overlay phase from the flow hook. */
  managePhase: AppointmentManagePhase;
  /** Appointment id with an in-flight confirm or cancel mutation, if any. */
  pendingAppointmentId: string | null;
  /** Floating UI positioning styles for the details card. */
  floatingStyles?: React.CSSProperties;
  /** Ref callback used by Floating UI for the details card. */
  floatingRef?: (node: HTMLElement | null) => void;
  /** Optional z-index override applied on top of `floatingStyles`. */
  popupZIndex?: number;
  /** Closes only the details popup, leaving other manage state intact. */
  onCloseDetails: () => void;
  /** Closes the full manage flow and clears active appointment state. */
  onCloseFlow: () => void;
  onConfirm: (appointment: Appointment) => void;
  onManualReschedule: (appointment: Appointment) => void;
  onCancel: (appointment: Appointment) => void;
  onMarkNoShow?: (appointment: Appointment) => void;
  onDelete?: (appointment: Appointment) => void;
  /** Optional AI reschedule handler; details close before it runs. */
  onRescheduleWithAI?: (appointment: Appointment) => void;
  onCloseManualReschedule: () => void;
  /** Saves the staff note; enables note editing in the details popup when provided. */
  onSaveNotes?: (appointment: Appointment, notes: string) => Promise<boolean>;
  /** Appointment id with an in-flight note save, if any. */
  savingNotesAppointmentId?: string | null;
}

/** Default z-index of the floating details popup, which sits above regular modals. */
const DEFAULT_POPUP_Z_INDEX = 1050;

/** Composes details popup, click-away backdrop, and manual reschedule dialog for one active appointment. */
export function AppointmentManageOverlays({
  activeAppointment,
  detailsOpen,
  detailsPositioned = true,
  managePhase,
  pendingAppointmentId,
  floatingStyles,
  floatingRef,
  popupZIndex,
  onCloseDetails,
  onCloseFlow,
  onConfirm,
  onManualReschedule,
  onCancel,
  onMarkNoShow,
  onDelete,
  onRescheduleWithAI,
  onCloseManualReschedule,
  onSaveNotes,
  savingNotesAppointmentId = null,
}: AppointmentManageOverlaysProps) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [noteEditOpen, setNoteEditOpen] = useState(false);

  if (!activeAppointment) {
    return null;
  }

  return (
    <ActiveAppointmentOverlays
      activeAppointment={activeAppointment}
      detailsOpen={detailsOpen}
      detailsPositioned={detailsPositioned}
      managePhase={managePhase}
      pendingAppointmentId={pendingAppointmentId}
      floatingStyles={floatingStyles}
      floatingRef={floatingRef}
      popupZIndex={popupZIndex}
      onCloseDetails={onCloseDetails}
      onCloseFlow={onCloseFlow}
      onConfirm={onConfirm}
      onManualReschedule={onManualReschedule}
      onCancel={onCancel}
      onMarkNoShow={onMarkNoShow}
      onDelete={onDelete}
      onRescheduleWithAI={onRescheduleWithAI}
      onCloseManualReschedule={onCloseManualReschedule}
      onSaveNotes={onSaveNotes}
      savingNotesAppointmentId={savingNotesAppointmentId}
      reviewOpen={reviewOpen}
      setReviewOpen={setReviewOpen}
      noteEditOpen={noteEditOpen}
      setNoteEditOpen={setNoteEditOpen}
    />
  );
}

interface ActiveAppointmentOverlaysProps extends AppointmentManageOverlaysProps {
  activeAppointment: Appointment;
  reviewOpen: boolean;
  setReviewOpen: (open: boolean) => void;
  noteEditOpen: boolean;
  setNoteEditOpen: (open: boolean) => void;
}

/** Overlay tree for a non-null active appointment, so hooks can depend on it. */
function ActiveAppointmentOverlays({
  activeAppointment,
  detailsOpen,
  detailsPositioned = true,
  managePhase,
  pendingAppointmentId,
  floatingStyles,
  floatingRef,
  popupZIndex,
  onCloseDetails,
  onCloseFlow,
  onConfirm,
  onManualReschedule,
  onCancel,
  onMarkNoShow,
  onDelete,
  onRescheduleWithAI,
  onCloseManualReschedule,
  onSaveNotes,
  savingNotesAppointmentId = null,
  reviewOpen,
  setReviewOpen,
  noteEditOpen,
  setNoteEditOpen,
}: ActiveAppointmentOverlaysProps) {
  const clientContact = useAppointmentClientContact(activeAppointment);

  return (
    <>
      <AppointmentDetailsPopup
        appointment={activeAppointment}
        visible={detailsOpen}
        isPositioned={detailsPositioned}
        onClose={onCloseFlow}
        onConfirm={onConfirm}
        onReviewChangeRequest={
          activeAppointment.activeChangeRequest
            ? () => setReviewOpen(true)
            : undefined
        }
        onReschedule={
          onRescheduleWithAI
            ? (appointment) => {
                onCloseDetails();
                onRescheduleWithAI(appointment);
              }
            : () => {}
        }
        onManualReschedule={onManualReschedule}
        onCancel={onCancel}
        onMarkNoShow={onMarkNoShow}
        onDelete={onDelete}
        confirmingAppointmentId={pendingAppointmentId}
        floatingStyles={
          popupZIndex != null
            ? {
                ...floatingStyles,
                zIndex: popupZIndex,
              }
            : floatingStyles
        }
        floatingRef={floatingRef}
        onEditNotes={onSaveNotes ? () => setNoteEditOpen(true) : undefined}
      />
      {onSaveNotes ? (
        <AppointmentNoteEditModal
          open={noteEditOpen}
          appointment={activeAppointment}
          clientName={clientContact.name}
          saving={savingNotesAppointmentId === activeAppointment.id}
          zIndex={(popupZIndex ?? DEFAULT_POPUP_Z_INDEX) + 10}
          onCancel={() => setNoteEditOpen(false)}
          onSave={(notes) => onSaveNotes(activeAppointment, notes)}
        />
      ) : null}
      {detailsOpen ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={onCloseDetails}
        />
      ) : null}
      <ManualRescheduleDialog
        visible={managePhase === 'manualReschedule'}
        appointment={activeAppointment}
        onCancel={onCloseManualReschedule}
      />
      <AppointmentChangeRequestReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        request={
          isRescheduleChangeRequest(activeAppointment.activeChangeRequest)
            ? activeAppointment.activeChangeRequest
            : null
        }
        appointment={activeAppointment}
      />
    </>
  );
}
